import { useContentStructure, useMedia, useSelectionReference, useStructureDirty } from "@/hooks/contextHooks";
import { NewContentItem, NewSection } from "@xnav/types/src/common";
import { ReactStateType } from "@xnav/types/src/frontend";
import FuzzyFinder, { FuzzyFindWrapperProps } from "./fuzzyFinder";
import { Button, Card, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { MediaMeta, MediaType } from "@/pages/mediaManager";
import MediaSelectionCard from "./mediaSelectionCard";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AudioPlayer from "./audioPlayer";
import { updateContentItemInTree } from "@/functions/editorFunctions";


const renderWrapper = <T,>({ data, renderComponentProps, RenderComponent }: FuzzyFindWrapperProps<T>) => (
	<Flex flexDir='column' w='100%' height='100%'>
		<SimpleGrid padding={3} w='full' flex={1} minChildWidth='300px' spacing={3}>
			{data.map((d, i) => <RenderComponent key={i} data={d} {...renderComponentProps} selected={false} />)}
		</SimpleGrid>
	</Flex>
)

export default function AudioEditor() {
	const [selectionReference, setSelectionReference] = useSelectionReference() as ReactStateType<NewContentItem>
	const [media, setMedia] = useMedia() as ReactStateType<MediaMeta[] | undefined>
	const [sectionMeta, setSectionMeta] = useContentStructure() as ReactStateType<NewSection[] | undefined>
	const [, setStructureDirty] = useStructureDirty() as ReactStateType<boolean>

	const audios = useMemo(() => {
		if (!media || !Array.isArray(media)) return []
		return media.filter(m => m.type === MediaType.Audio)
	}, [media])
	const [audio, setAudio] = useState<MediaMeta>()

	useEffect(() => {
		if (!selectionReference || selectionReference.type !== 'audio' || !media || !Array.isArray(media)) return
		setAudio(media.find(m => m.id === selectionReference.fileId || m.references?.includes(selectionReference.id) || m.name === selectionReference.title || m.id === selectionReference.id))
	}, [selectionReference, media])

	if (!selectionReference || selectionReference.type !== 'audio') return <></>

	if (!selectionReference.fileId) return (
		<Flex flex={1} w='100%' h='100%' minH={0} p={3} overflow='hidden'>
			<FuzzyFinder
				data={audios}
				searchKeys={['name', 'type']}
				RenderComponent={MediaSelectionCard}
				renderComponentProps={{
					onAdd: (id: string) => {
						setSelectionReference({ ...selectionReference, fileId: id })
						if (setSectionMeta) {
							setSectionMeta(prev => prev ? updateContentItemInTree(prev, selectionReference.id, { fileId: id }) : prev)
						}
						setStructureDirty(true)
						if (setMedia && media) {
							setMedia(media.map(m => m.id === id ? { ...m, references: [...(m.references || []), selectionReference.id] } : m))
						}
					}
				}}
				RenderWrapper={renderWrapper}
			>
				<></>
			</FuzzyFinder>
		</Flex>
	)
	return (
		<Flex flex={1} w='100%' h='100%' minH={0} flexDir='column' p={3}>
			<Card variant='outline' flex={1} w='100%' overflowY='scroll' justifyContent='center'>
				{audio ? (
					<>
						<Text>Play {audio.name} </Text>
						<AudioPlayer audioUrl={audio.fileUrl!} />
					</>
				) : (<></>)}
			</Card>
			<Button
				mt={3}
				leftIcon={<FontAwesomeIcon icon={faTrash} />}
				onClick={() => {
					const oldFileId = (selectionReference as any).fileId
					setSelectionReference({ ...selectionReference, fileId: undefined })
					if (setSectionMeta) {
						setSectionMeta(prev => prev ? updateContentItemInTree(prev, selectionReference.id, { fileId: undefined }) : prev)
					}
					setStructureDirty(true)
					if (setMedia && media && oldFileId) {
						setMedia(media.map(m => m.id === oldFileId ? { ...m, references: (m.references || []).filter(r => r !== selectionReference.id) } : m))
					}
				}}
			>Change Audio</Button>
		</Flex>
	)
}
